import { Link } from 'react-router-dom';
import {
  ICP_CODING_ID,
  ARCHITECTURE_COMPLIANCE,
  REJECTED_TECHNOLOGIES,
  BACKEND_STANDARDS,
  GO_CODING_RULES,
  FRONTEND_STANDARDS,
  REACT_RULES,
  DATABASE_STANDARDS,
  API_STANDARDS,
  EVENT_STANDARDS,
  AI_STANDARDS,
  MARKETPLACE_STANDARDS,
  SECURITY_STANDARDS,
  LOGGING_STANDARDS,
  ERROR_STANDARDS,
  TESTING_STANDARDS,
  DOCUMENTATION_STANDARDS,
  GIT_STANDARDS,
  CICD_STANDARDS,
  CODE_QUALITY_STANDARDS,
  PERFORMANCE_STANDARDS,
  DEFINITION_OF_READY,
  DEFINITION_OF_DONE,
  AI_CODE_GENERATION_RULES,
  COMPLIANCE_CHECKLIST,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_TECHNOLOGY_LOCK,
  DEMO_BACKEND_GO,
  DEMO_FRONTEND_REACT,
  DEMO_DATA_API_EVENT,
  DEMO_AI_MARKETPLACE,
  DEMO_SECURITY_LOGGING,
  DEMO_TESTING_CI,
  DEMO_EXECUTIVE,
  buildComplianceDashboard,
} from '../../codingconstitution/framework';
import {
  TechnologyLockPanel,
  BackendGoStandardsPanel,
  FrontendReactStandardsPanel,
  DatabaseAPIEventPanel,
  AIMarketplaceStandardsPanel,
  SecurityLoggingErrorPanel,
  TestingDocumentationCIPanel,
  ExecutiveComplianceDashboardPanel,
} from '../../components/codingconstitution';
import { Card, KpiCard } from '../../components/ui';

export function CodingConstitutionPage() {
  const dashboard = buildComplianceDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{ICP_CODING_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Enterprise Coding Constitution</h1>
        <p className="mt-1 text-sm text-slate-500">
          Mandatory implementation standards for every source file, SQL script, API, test, Docker artifact,
          CI/CD workflow, and AI-generated implementation. NO production code SHALL violate this constitution.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 dark:border-indigo-900 dark:bg-indigo-950 dark:text-indigo-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
        <KpiCard label="Technology Lock" value={dashboard.technology_areas.toString()} />
        <KpiCard label="Backend Standards" value={dashboard.backend_standards.toString()} />
        <KpiCard label="Frontend Standards" value={dashboard.frontend_standards.toString()} />
        <KpiCard label="Database/API/Event" value={dashboard.database_standards.toString()} />
        <KpiCard label="Security Standards" value={dashboard.security_standards.toString()} />
        <KpiCard label="Governance Areas" value={dashboard.governance_areas.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Coding Architecture</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-400">Compliance: {ARCHITECTURE_COMPLIANCE.join(' · ')}</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Technology Lock (TL-1)</h2>
        <p className="mt-1 text-sm text-slate-500">Approved stack — rejected: {REJECTED_TECHNOLOGIES.slice(0, 4).join(', ')}…</p>
        <Card className="mt-4" padding="sm">
          <TechnologyLockPanel items={DEMO_TECHNOLOGY_LOCK} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Backend & Go Coding Standards</h2>
        <p className="mt-1 text-sm text-slate-500">{BACKEND_STANDARDS.slice(0, 8).join(' · ')}…</p>
        <p className="mt-1 text-xs text-slate-400">{GO_CODING_RULES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <BackendGoStandardsPanel items={DEMO_BACKEND_GO} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Frontend & React Standards</h2>
        <p className="mt-1 text-sm text-slate-500">{FRONTEND_STANDARDS.slice(0, 8).join(' · ')}…</p>
        <p className="mt-1 text-xs text-slate-400">{REACT_RULES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <FrontendReactStandardsPanel items={DEMO_FRONTEND_REACT} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Database, API & Event Standards</h2>
        <p className="mt-1 text-sm text-slate-500">{DATABASE_STANDARDS.slice(0, 6).join(' · ')}… · {API_STANDARDS.slice(0, 5).join(' · ')}…</p>
        <p className="mt-1 text-xs text-slate-400">{EVENT_STANDARDS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <DatabaseAPIEventPanel items={DEMO_DATA_API_EVENT} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI & Marketplace Standards</h2>
        <p className="mt-1 text-sm text-slate-500">{AI_STANDARDS.slice(0, 6).join(' · ')}… · {MARKETPLACE_STANDARDS.slice(0, 6).join(' · ')}…</p>
        <Card className="mt-4" padding="sm">
          <AIMarketplaceStandardsPanel items={DEMO_AI_MARKETPLACE} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Security, Logging & Error Handling</h2>
        <p className="mt-1 text-sm text-slate-500">{SECURITY_STANDARDS.slice(0, 6).join(' · ')}…</p>
        <p className="mt-1 text-xs text-slate-400">{LOGGING_STANDARDS.join(' · ')} · {ERROR_STANDARDS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <SecurityLoggingErrorPanel items={DEMO_SECURITY_LOGGING} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Testing, Documentation, Git & CI/CD</h2>
        <p className="mt-1 text-sm text-slate-500">{TESTING_STANDARDS.slice(0, 5).join(' · ')}… · {DOCUMENTATION_STANDARDS.slice(0, 5).join(' · ')}…</p>
        <p className="mt-1 text-xs text-slate-400">{GIT_STANDARDS.join(' · ')} · {CICD_STANDARDS.join(' · ')} · {CODE_QUALITY_STANDARDS.join(' · ')} · {PERFORMANCE_STANDARDS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <TestingDocumentationCIPanel items={DEMO_TESTING_CI} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Definition of Ready, Done & Compliance</h2>
        <p className="mt-1 text-sm text-slate-500">DoR: {DEFINITION_OF_READY.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">DoD: {DEFINITION_OF_DONE.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">AI Code Rules: {AI_CODE_GENERATION_RULES.join(' · ')} · Checklist: {COMPLIANCE_CHECKLIST.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ExecutiveComplianceDashboardPanel items={DEMO_EXECUTIVE} />
        </Card>
      </section>
    </div>
  );
}
